import { useMemo, useState } from "react";
import { Heart, Sparkles } from "lucide-react";
import { PetAvatar } from "@/components/PetAvatar";
import { UserLink } from "@/components/UserBits";

const KIND = { hund: ["Hund", "from-amber-100 to-orange-200"], katze: ["Katze", "from-violet-100 to-fuchsia-200"], hase: ["Hase", "from-sky-100 to-cyan-200"],
  drache: ["Drache", "from-emerald-100 to-teal-200"], itzling: ["Itzling", "from-rose-100 to-pink-200"] };
const SORTS = [["top", "Beliebteste"], ["new", "Neueste"]];

function GalleryTile({ p, onLike, i }) {
  const [label, bg] = KIND[p.kind] || ["Haustier", "from-rose-100 to-pink-200"];
  return (
    <div className={`relative rounded-3xl bg-gradient-to-br ${bg} p-3 sm:p-4 text-center fade-up`} style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }} data-testid={`gallery-pet-${p.owner.user_id}`}>
      <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-full bg-white/70 text-[10px] font-extrabold text-slate-600">{label}</span>
      <PetAvatar kind={p.kind} name={p.name} equipped={p.equipped} className="w-24 h-24 sm:w-28 sm:h-28 mx-auto ring-4 ring-white/70 rounded-full" />
      <p className="font-display font-extrabold mt-2 truncate">{p.name}</p>
      <p className="text-[11px] text-slate-600 truncate">von <UserLink id={p.owner.user_id}>{p.owner.name}</UserLink></p>
      <button onClick={() => onLike(p)} disabled={p.me} aria-pressed={p.liked} data-testid={`gallery-like-${p.owner.user_id}`}
        className={`mt-2.5 h-9 px-3.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 transition-[transform,background-color] active:scale-90 disabled:opacity-60 ${p.liked ? "pink-gradient text-white" : "bg-white/80 text-rose-600 hover:bg-white"}`}>
        <Heart className={`w-3.5 h-3.5 ${p.liked ? "fill-white" : ""}`} /> <span data-testid={`gallery-likes-${p.owner.user_id}`}>{p.total_likes}</span>
      </button>
    </div>
  );
}

export function PetGallery({ pets, onLike }) {
  const [kind, setKind] = useState("all");
  const [sort, setSort] = useState("top");
  const kinds = useMemo(() => [...new Set(pets.map((p) => p.kind))], [pets]);
  const list = useMemo(() => pets.filter((p) => kind === "all" || p.kind === kind)
    .sort((a, b) => (sort === "top" ? b.total_likes - a.total_likes : (b.born_at || "").localeCompare(a.born_at || ""))), [pets, kind, sort]);
  const chip = (on) => `h-9 px-3.5 rounded-full text-xs font-bold transition-colors ${on ? "pink-gradient text-white" : "bg-white border border-rose-100 text-slate-600 hover:border-rose-300"}`;
  return (
    <section className="space-y-4" data-testid="pet-gallery">
      <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-rose-500" /><h2 className="font-display text-xl font-extrabold flex-1">Galerie aller Haustiere</h2><span className="text-xs font-bold text-slate-400">{list.length} Tiere</span></div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setKind("all")} className={chip(kind === "all")} data-testid="gallery-kind-all">Alle</button>
        {kinds.map((k) => <button key={k} onClick={() => setKind(k)} className={chip(kind === k)} data-testid={`gallery-kind-${k}`}>{KIND[k]?.[0] || k}</button>)}
        <span className="flex-1" />
        {SORTS.map(([k, l]) => <button key={k} onClick={() => setSort(k)} className={chip(sort === k)} data-testid={`gallery-sort-${k}`}>{l}</button>)}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="gallery-grid">
        {list.map((p, i) => <GalleryTile key={p.owner.user_id} p={p} i={i} onLike={onLike} />)}
      </div>
      <p className="text-[11px] text-center text-slate-400">Die Herzen zählen auch für das „Schönste Outfit der Woche“.</p>
    </section>
  );
}
